/**
 * AtlasAmbienceModule — condition-driven ambient beds + UI/movement SFX
 * (Mirelo-generated clips under /audio/<key>.mp3, see
 * tools/asset-pipeline/gen-ambience.mjs).
 *
 * One BED loops at a time, chosen every half second and CROSSFADED on
 * change (priority: underwater > space > settlement city/town > biome).
 * Two additional layers ride on top: a versona-proximity MURMUR whose gain
 * tracks how many versonas stand within earshot, and a footsteps loop
 * (grass or stone by settlement proximity) while the avatar actually moves.
 * UI one-shots: window open/close (CustomEvent from ui_window) + a soft
 * click on any button press.
 *
 * The SELECTION state machine runs regardless of audio availability — a
 * missing clip just means silence for that slot (probes assert selection,
 * not sound).
 *
 * Since 2026-09-20 this module owns NO AudioContext and no cache: it is a
 * client of the shared runtime (lib/audio/runtime.ts). The bed rides the
 * `ambience` bus, the footsteps `playerFoley`, the clicks `ui` — three
 * categories that used to share one private master and no volume control.
 */
import type { App, IModule } from 'enginex';
import { audioRuntime, type LoopHandle } from '../lib/audio/runtime.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { worldEnv } from '../lib/world_env.js';
import { biomeAt, type Biome } from '@shared/terrain.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { BIOME_FOOT_SURFACE, playerSteps } from '../lib/audio/footfalls.js';

// Per-biome footstep surface (settlements pave over to steps_stone). New keys
// steps_sand/steps_mud/steps_regolith are generated by gen-sfx.mjs; a missing
// file falls back to silence gracefully, so wiring can land before the audio.
// ONE table (lib/audio/footfalls.ts) serves these recorded loops AND the synthesised steps of everyone else, so a
// versona beside you never walks on a different ground than you do.
const stepLoopFor = (b: Biome): string => `steps_${BIOME_FOOT_SURFACE[b]}`;

const BED_GAIN = 0.32;
const MURMUR_MAX_GAIN = 0.4;
const STEPS_GAIN = 0.5;
const UI_GAIN = 0.2;
const CLICK_GAIN = 0.12;
const CROSSFADE_S = 2.5;
const STEP_RAMP_S = 0.15;
// THE CACHE-BUST LAW: the bed version (`?v=`) now lives with the asset resolver — ASSET_VERSIONS in lib/audio/runtime.ts.
// Bump it THERE on every bed regen (tools/asset-pipeline/gen-ambience.mjs).
/** Versonas within this range feed the murmur layer. */
const MURMUR_RANGE_M = 25;
/** Fewer versonas than this is company, not a crowd — no murmur. */
const MURMUR_MIN_GROUP = 3;
/**
 * Ambience carries NO people noises (2026-08-21): the murmur loop was the one
 * voice source left in cities (a generated "crowd" can still render a clear
 * word — "five"). Off by default; the layer code stays for a future opt-in.
 */
const MURMUR_ENABLED = false;
/** A settlement center within this range means stone underfoot. */
const STONE_RANGE_M = 200;
/** City/town ambience radii by tier (hamlets read as towns). */
const TIER_RADIUS: Record<string, number> = { city: 420, town: 280, hamlet: 170, capital: 3230, district: 420, circuit: 280 }; // capital wave

interface Layer {
  key: string;
  loop: LoopHandle;
  /** The gain last asked for (probe surface — the node itself belongs to the runtime). */
  gain: number;
}
/** Settlement tiers whose scale is a CITY's (2026-09-20: capitals and their districts fell through to the TOWN bed). */
const CITY_BED_TIERS = new Set(['city', 'capital', 'district']);

export class AtlasAmbienceModule implements IModule {
  readonly name = 'AtlasAmbience';

  /** Keys this module has asked the shared store for (the probe's `buffers` list). */
  private readonly asked = new Set<string>();
  private app: App | null = null;
  private physics: { grounded: boolean; waterMode: string } | null | undefined;

  private bed: Layer | null = null;
  private bedKey = ''; // desired (may lead the playing layer while its clip loads)
  private murmur: Layer | null = null;
  private steps: Layer | null = null;
  private stepsKey = '';

  private sites: SettlementSite[] = [];
  private sitesSeed = '';
  private acc = 0;

  init(app: App): void {
    this.app = app;
    // (The AudioContext is the shared runtime's: born and resumed on any gesture — lib/audio/runtime.ts.)
    // UI one-shots: window chrome events + a soft click on any button. `ui_click` has a synthesized stand-in, so
    // the FIRST click of a session is never silent (the first-play miss).
    window.addEventListener('atlas-ui-sound', (e) => {
      const kind = (e as CustomEvent<string>).detail;
      audioRuntime().playKey(kind === 'open' ? 'ui_open' : 'ui_close', { gain: UI_GAIN });
    });
    window.addEventListener('click', (e) => {
      const t = e.target;
      if (t instanceof Element && t.closest('button')) audioRuntime().playKey('ui_click', { gain: CLICK_GAIN });
    });

    app.world.addSystem(makeSystem('AtlasAmbience', Phase.UI, 60, (dt) => {
      this.acc += dt;
      if (this.acc < 0.5) return;
      this.acc = 0;
      this.tick();
    }));

    // Probe surface: the selection state, independent of audio availability.
    (globalThis as unknown as { __ambience?: () => object }).__ambience = () => ({
      bed: this.bedKey,
      playingBed: this.bed?.key ?? null,
      murmurGain: this.murmur ? Math.round(this.murmur.gain * 100) / 100 : 0,
      steps: this.stepsKey,
      stepsBlockedBy: this.stepsBlockedBy,
      ctx: audioRuntime().ctx?.state ?? 'uninitialised',
      buffers: [...this.asked].map((k) => { const st = audioRuntime().store.state(`a:${k}`); return `${k}:${st === 'ready' ? 'ok' : st === 'failed' ? 'missing' : st}`; }),
    });
    (globalThis as unknown as { __bedAt?: (x: number, z: number) => string | null }).__bedAt = (x, z) => this.bedAt(atlas.conn, x, z);
    (globalThis as unknown as { __settlementSites?: () => Array<{ x: number; z: number; tier: string }> }).__settlementSites = () => { this.bedAt(atlas.conn, 0, 0); return this.sites.map((s) => ({ x: s.centerX, z: s.centerZ, tier: s.tier })); };
  }

  // ── Selection ─────────────────────────────────────────────────────────────

  private tick(): void {
    const conn = atlas.conn;
    const desired = this.desiredBed(conn);
    this.bedKey = desired ?? '';
    if (desired) this.ensureBed(desired);
    else this.stopBed();
    this.updateMurmur(conn);
    this.updateSteps();
  }

  /** Priority: underwater > space > settlement > biome (commons) / world seed. */
  private desiredBed(conn: typeof atlas.conn): string | null {
    if (atlas.currentWorldId === null) return null;
    if (atlas.submerged) return 'ambient_underwater';
    if (atlas.spaceFlight) return 'ambient_space';
    // Edit Mode ENVIRONMENT: an authored ambience bed outranks the biome pick.
    const envKey = worldEnv().ambienceKey;
    if (envKey) return envKey;
    return this.bedAt(conn, atlas.localX, atlas.localZ);
  }

  /** The settlement/biome bed the world would play at (x, z) — also the probe's "is this spot a settlement?" oracle. */
  private bedAt(conn: typeof atlas.conn, x: number, z: number): string | null {
    const seed = this.worldSeed(conn);
    if (!seed) return null;
    if (atlas.currentWorldId === atlas.commonsWorldId) {
      if (this.sitesSeed !== seed) {
        this.sites = settlementSites(seed);
        this.sitesSeed = seed;
      }
      let best: SettlementSite | null = null;
      let bestD = Infinity;
      for (const s of this.sites) {
        const d = Math.hypot(s.centerX - x, s.centerZ - z);
        if (d < (TIER_RADIUS[s.tier] ?? 0) && d < bestD) { bestD = d; best = s; }
      }
      if (best) return CITY_BED_TIERS.has(best.tier) ? 'ambient_city' : 'ambient_town';
    }
    return `ambient_${biomeAt(seed, x, z)}`;
  }

  private worldSeed(conn: typeof atlas.conn): string | null {
    if (!conn || atlas.currentWorldId === null) return null;
    for (const w of conn.db.world.iter()) {
      if (w.worldId === atlas.currentWorldId) return w.terrainSeed;
    }
    return null;
  }

  // ── Layers ────────────────────────────────────────────────────────────────

  /** Until this moment (ms) the crossfade owns the bed's gain; afterwards every tick re-asserts it. */
  private bedFadeEndsAt = 0;

  private ensureBed(key: string): void {
    if (this.bed?.key === key) {
      // Keep the bed ALIVE: a voice the allocator took (or a context that was suspended when the bed began) restarts
      // on the next ask — and the level now follows a live change of the world's ambience gain, not only a bed change.
      if (performance.now() > this.bedFadeEndsAt) this.drive(this.bed, BED_GAIN * worldEnv().ambienceGain, 0.5);
      return;
    }
    const buf = this.buffer(key);
    if (!buf) return; // still loading / missing — keep the old bed playing
    const old = this.bed;
    const layer = this.startLoop(key, 'ambience-bed');
    this.bed = layer;
    this.bedFadeEndsAt = performance.now() + CROSSFADE_S * 1000;
    this.drive(layer, BED_GAIN * worldEnv().ambienceGain, CROSSFADE_S);
    if (old) old.loop.stop(CROSSFADE_S); // the crossfade: the old bed fades under the new one and is released
  }

  private stopBed(): void {
    if (!this.bed) return;
    this.bed.loop.stop(CROSSFADE_S);
    this.bed = null;
  }

  private updateMurmur(conn: typeof atlas.conn): void {
    let near = 0;
    // A CROWD layer, not a presence cue: only under a settlement bed and only for a group — one or two
    // wandering versonas out in a biome used to start "a small crowd murmuring" in the wilderness.
    const inSettlement = MURMUR_ENABLED && (this.bedKey === 'ambient_city' || this.bedKey === 'ambient_town');
    if (inSettlement && conn && atlas.currentWorldId !== null && !atlas.spaceFlight && !atlas.submerged) {
      try {
        for (const v of conn.db.versona.iter()) {
          if (Math.hypot(v.curX - atlas.localX, v.curZ - atlas.localZ) < MURMUR_RANGE_M) near++;
          if (near >= 8) break;
        }
      } catch { /* versona table not subscribed in this world */ }
    }
    const target = near >= MURMUR_MIN_GROUP ? Math.min(MURMUR_MAX_GAIN, near * 0.06) : 0;
    if (target > 0 && !this.murmur) {
      const buf = this.buffer('versona_murmur');
      if (buf) this.murmur = this.startLoop('versona_murmur', 'ambience-murmur');
    }
    if (this.murmur) this.drive(this.murmur, target, 0.8);
  }

  private updateSteps(): void {
    // Keyed off the HELD INTENT (the input truth) — position speed is a
    // derived signal that under-reads on slow frames (headless sim, hitches).
    // FOOTSTEPS ARE CONTACTS, NOT A HELD KEY (2026-09-20). The loop used to run on intent alone, minus three flags —
    // so holding W in a hovercraft or a ship in atmosphere walked along underneath you, and a jump kept stepping in
    // mid-air. The body's own state decides now: on foot, feet down, not swimming.
    if (this.physics === undefined) this.physics = (this.app?.getModule('PhysicsModule') as typeof this.physics) ?? undefined;
    const intent = Math.hypot(atlas.intentX, atlas.intentZ) > 0.1 && atlas.currentWorldId !== null;
    const blocked = !intent ? '' : atlas.spaceFlight ? 'space' : atlas.flyMode ? 'fly' : atlas.submerged ? 'submerged'
      : atlas.hoverRide ? 'riding' : atlas.skyFlight ? 'flying'
        : this.physics && this.physics.waterMode === 'swim' ? 'swimming' : this.physics && !this.physics.grounded ? 'airborne' : '';
    // The body's state WITHOUT the key: contact-driven steps (below) follow real movement, not a held W.
    const onFoot = atlas.currentWorldId !== null && !atlas.spaceFlight && !atlas.flyMode && !atlas.submerged && !atlas.hoverRide && !atlas.skyFlight
      && !(this.physics && this.physics.waterMode === 'swim') && !(this.physics && !this.physics.grounded);
    let want = '';
    if (onFoot) {
      let stone = false;
      if (atlas.currentWorldId === atlas.commonsWorldId && this.sites.length > 0) {
        for (const s of this.sites) {
          if (Math.hypot(s.centerX - atlas.localX, s.centerZ - atlas.localZ) < STONE_RANGE_M) { stone = true; break; }
        }
      }
      if (stone) {
        want = 'steps_stone';
      } else {
        const seed = this.worldSeed(atlas.conn);
        want = seed ? stepLoopFor(biomeAt(seed, atlas.localX, atlas.localZ)) : 'steps_grass';
      }
    }
    // CONTACT STEPS (2026-09-20): when recorded one-shot takes exist for this ground (audio/variants.json), my steps are
    // footfalls that FOLLOW PACE — played per stride by FootprintDecalModule — and this loop stands down. No takes for
    // the ground → the recorded loop below, exactly as before.
    const ps = playerSteps();
    const surface = want === '' ? ps.surface : (want.slice('steps_'.length) as typeof ps.surface);
    ps.onFoot = onFoot;
    ps.surface = surface;
    ps.contact = onFoot && audioRuntime().variantCount(`foot_${surface}`) > 0;
    if (ps.contact) { want = ''; this.stepsBlockedBy = 'contact'; }
    else { this.stepsBlockedBy = blocked; if (!(intent && blocked === '')) want = ''; }
    this.stepsKey = want;
    if (want === '' && this.steps) {
      this.steps.loop.stop(STEP_RAMP_S);
      this.steps = null;
      return;
    }
    if (want !== '' && this.steps?.key !== want) {
      const old = this.steps;
      const buf = this.buffer(want);
      if (!buf) return;
      this.steps = this.startLoop(want, 'ambience-steps');
      this.drive(this.steps, STEPS_GAIN, STEP_RAMP_S);
      if (old) old.loop.stop(STEP_RAMP_S);
    } else if (this.steps) {
      this.drive(this.steps, STEPS_GAIN, STEP_RAMP_S); // re-asserted every tick: a stolen voice comes back
    }
  }
  /** Why the footstep loop is held off while a movement key is down ('' = it may play) — probe surface. */
  private stepsBlockedBy = '';

  // ── WebAudio plumbing ─────────────────────────────────────────────────────

  /** The decoded clip if the shared store has it NOW; otherwise the load starts (and is retried on a transient failure). */
  private buffer(key: string): AudioBuffer | null {
    this.asked.add(key);
    const rt = audioRuntime();
    return rt.ctx ? rt.store.get(`a:${key}`) : null; // no context yet (no gesture): nothing can decode — the next tick asks again
  }

  /** A layer is a SCOPED runtime loop: the bus comes from the key (`ambient_*` → ambience, `steps_*` → playerFoley). */
  private startLoop(key: string, scope: string): Layer {
    return { key, loop: audioRuntime().loop(scope, key), gain: 0 };
  }

  private drive(layer: Layer, to: number, seconds: number): void {
    layer.gain = to;
    layer.loop.set(to, 1, seconds);
  }

  dispose(): void {
    for (const l of [this.bed, this.murmur, this.steps]) l?.loop.stop(0.1);
    // (The context is the shared runtime's — a module never closes it.)
  }
}
